import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { FileText, Eye } from "lucide-react";
import AdminObservationsModal from "../../../modals/admin/AdminObservationsModal";
import ViewAdminObservationsModal from "../../../modals/admin/ViewAdminObservationsModal";
import { fetchAdminObservationsByTeacher } from "../../../redux/adminObservationSlice";

const TeacherDetails = ({ teacher }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isObservationsModalOpen, setIsObservationsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const { campusClassesDetail } = useSelector((state) => state?.campuses || {});
  const { observations, loading } = useSelector((state) => state.adminObservation);

  useEffect(() => {
    if (teacher?.teacher?.email) {
      dispatch(fetchAdminObservationsByTeacher(teacher.teacher.email));
    }
  }, [teacher?.teacher?.email, dispatch]);

  return (
    <>
    <div className="bg-gray-50 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {/* Personal Details Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-bold text-blue-500 mb-4">
          {t("PersonalDetails")}
        </h2>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="font-medium text-gray-600">{t("Location")}:</td>
              <td>{teacher.teacher?.location}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("JoiningDate")}:</td>
              <td>{teacher.teacher?.joiningDate}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("CNIC")}:</td>
              <td>{teacher.teacher?.cnic}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("MaritalStatus")}:</td>
              <td>{teacher.teacher?.maritalStatus}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Academics Performance Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-bold text-blue-500 mb-4">
          {t("AcademicsPerformance")}
        </h2>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="font-medium text-gray-600">{t("CourseLevel")}:</td>
              <td>{teacher.academicPerformance?.courseLevel}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("FirstSemResult")}:</td>
              <td>{teacher.academicPerformance?.firstSemResult}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("SecondSemResult")}:</td>
              <td>{teacher.academicPerformance?.secondSemResult}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("OverallAnnualResult")}:</td>
              <td>{teacher.academicPerformance?.overallAnnualResult}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Assigned Classes Card */}
      <div className="bg-white shadow rounded-lg p-6 h-60 overflow-y-auto">
        <h2 className="text-lg font-bold text-blue-500 mb-4">
          {t("AssignedClasses")}
        </h2>
        {teacher.assignedClasses?.map((classItem, idx) => (
          <div key={idx} className="mb-4 border-b border-gray-200 pb-4">
            <p className="font-bold text-gray-800">
              {t("Class")} {classItem.className}
            </p>
            {classItem.subjects?.map((subject, detailIdx) => (
              <div key={detailIdx}>
                <p>
                  <span className="font-medium text-gray-600">{t("Section")} : </span>
                  {subject.sectionName}
                </p>
                <p>
                  <span className="font-medium text-gray-600">{t("Subject")} : </span>
                  {subject.subjectName}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Professional Experience */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-bold text-blue-500 mb-4">
          {t("ProfessionalExperience")}
        </h2>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="font-medium text-gray-600">{t("Organization")}:</td>
              <td>{teacher.professionalExperience?.organizationName}</td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("Designation")}:</td>
              <td>{teacher.professionalExperience?.designation}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Teacher Availability Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-bold text-blue-500 mb-4">
          {t("TeacherAvailability")}
        </h2>
        <table className="w-full text-sm">
          <tbody>
            <tr>
              <td className="font-medium text-gray-600">{t("DaysAvailable")}:</td>
              <td>
                {teacher.availability?.days?.length > 0
                  ? teacher.availability.days.join(", ")
                  : "Not specified"}
              </td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("TimeSlots")}:</td>
              <td>
                {teacher.availability?.fromTime && teacher.availability?.toTime
                  ? `${teacher.availability.fromTime.slice(0, 5)} - ${teacher.availability.toTime.slice(
                      0,
                      5
                    )}`
                  : "Not specified"}
              </td>
            </tr>
            <tr>
              <td className="font-medium text-gray-600">{t("MaxHoursPerWeek")}:</td>
              <td>
                {teacher.availability?.maxHoursPerWeek
                  ? `${teacher.availability.maxHoursPerWeek} hours`
                  : "Not specified"}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Admin Observations Card */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <FileText className="text-blue-500" size={24} />
            <h2 className="text-lg font-bold text-blue-500">
              {t("Observations") || "Admin Observations"}
            </h2>
          </div>
          <button 
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsObservationsModalOpen(true);
            }}
            className="bg-blue-500 text-white px-3 py-1.5 rounded hover:bg-blue-600 transition-colors text-sm"
          >
            {t("+") || "+"}
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : observations && observations.length > 0 ? (
          <div className="space-y-2">
            <div 
              className="cursor-pointer p-3 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
              onClick={() => setIsViewModalOpen(true)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="text-blue-500" size={18} />
                  <span className="font-medium text-gray-700">
                    {observations.length} {t("Observation") || "Observation"}{observations.length > 1 ? "s" : ""}
                  </span>
                </div>
                <span className="text-sm text-gray-500">{t("ClickToView") === "ClickToView" ? "Click to view" : (t("ClickToView") || "Click to view")}</span>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {t("LastObservation") === "LastObservation" ? "Last observation" : (t("LastObservation") || "Last observation")}: {observations[0]?.basicInformation?.observationDate 
                ? new Date(observations[0].basicInformation.observationDate).toLocaleDateString() 
                : "N/A"}
            </div>
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-sm text-gray-600 mb-2">
              {t("NoObservationsYet") || "No observations yet"}
            </p>
            <button
              onClick={() => setIsObservationsModalOpen(true)}
              className="text-blue-500 hover:text-blue-700 text-sm font-medium"
            >
              {t("AddFirstObservation") || "Add first observation"}
            </button>
          </div>
        )}
      </div>
      </div>
    </div>

    {/* Admin Observations Modal - Add New */}
    {isObservationsModalOpen && (
      <AdminObservationsModal
        isOpen={isObservationsModalOpen}
        onClose={() => {
          setIsObservationsModalOpen(false);
          // Refresh observations after adding
          if (teacher?.teacher?.email) {
            dispatch(fetchAdminObservationsByTeacher(teacher.teacher.email));
          }
        }}
        teacherEmail={teacher.teacher?.email}
        campusClasses={campusClassesDetail}
      />
    )}

    {/* View Admin Observations Modal */}
    {isViewModalOpen && (
      <ViewAdminObservationsModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        teacherEmail={teacher.teacher?.email}
        teacherDisplayName={
          `${teacher.teacher?.firstName || ""} ${teacher.teacher?.lastName || ""}`.trim() ||
          teacher.teacher?.email
        }
        campusClasses={campusClassesDetail}
        onAddNew={() => {
          setIsViewModalOpen(false);
          setIsObservationsModalOpen(true);
        }}
      />
    )}
    </>
  );
};

export default TeacherDetails; 